import { useState, useRef } from 'preact/hooks';
import { html } from '@shared/utils/rendering/html';
import { AdvancedFilters } from '@shared/components/advanced-filters';
import Chip from '@shared/components/chips';
import { Icon, IconNames } from '@shared/components/icons';
import Modal from '@shared/components/modals';
import Button from '@shared/components/buttons';
import {
    SarbSearchOptions,
    SarbFilterDefaults,
    SarbFilterGroups,
} from '@core/configs/filters/dashboard/sarb-filters.config';
import { RatingBands, RatingsMap } from '@core/constants/chips/ratings.constants';
import './sarb-advanced-filters.scss';

const normalizeSearchOption = (searchOption) => {
    const rawValue = searchOption?.value ?? searchOption;
    const normalized = (rawValue ?? 'all').toString().toLowerCase();

    const optionMap = {
        0: 'all',
        all: 'all',
        1: 'question',
        question: 'question',
        2: 'principle',
        principle: 'principle',
        3: 'paragraph',
        paragraph: 'paragraph',
    };

    return optionMap[normalized] ?? 'all';
};

// Description copy per band, keyed by the same RatingsMap values RatingBands carries. Range and
// colour come from RatingBands (ratings.constants.js) - the single source of truth also used by
// getRatingBand and the advanced filter's band matching - so the legend can't drift out of sync
// with actual chip colours the way it previously did.
const RATING_DESCRIPTIONS = {
    [RatingsMap.green]: 'Principal requirements are fully compliant',
    [RatingsMap.yellow]:
        'Principal requirements are largely compliant with only minor actions needed to be fully compliant',
    [RatingsMap.amber]:
        'Principal requirements non-compliant and significant actions are needed for compliance',
    [RatingsMap.red]: 'Not yet implemented',
};

const RATINGS_EXPLAINED = [...RatingBands].reverse().map((band) => ({
    label: band.rangeLabel,
    css: band.css,
    description: RATING_DESCRIPTIONS[band.value],
}));

export const SarbAdvancedFilters = ({ onFilterChange }) => {
    const filterRef = useRef(null);
    const [showRatingsModal, setShowRatingsModal] = useState(false);
    const [currentSearch, setCurrentSearch] = useState({ term: '', option: null });

    const openRatingsModal = () => setShowRatingsModal(true);
    const closeRatingsModal = () => setShowRatingsModal(false);

    const ratingsExplainedButton = html`
        <${Button}
            buttonType="tertiary"
            label=${{ precedent: html`<${Icon} name=${IconNames.Help} />`, text: 'RATINGS EXPLAINED' }}
            onClick=${openRatingsModal}
        />
    `;

    const handleFilter = (selectedFilters, searchTerm, searchOption) => {
        onFilterChange?.({
            principle: selectedFilters?.[SarbFilterGroups.principle]?.value ?? '',
            rating: selectedFilters?.[SarbFilterGroups.rating]?.value ?? '',
            searchTerm: searchTerm ?? currentSearch.term,
            searchOption: normalizeSearchOption(searchOption ?? currentSearch.option),
        });
    };

    const handleSearch = (searchTerm, searchOption) => {
        const normalizedSearchOption = normalizeSearchOption(searchOption);
        setCurrentSearch({ term: searchTerm ?? '', option: normalizedSearchOption });
        onFilterChange?.({
            principle:
                filterRef.current?.activeFilters?.selectedFilters?.[SarbFilterGroups.principle]
                    ?.value ?? '',
            rating:
                filterRef.current?.activeFilters?.selectedFilters?.[SarbFilterGroups.rating]
                    ?.value ?? '',
            searchTerm: searchTerm ?? '',
            searchOption: normalizedSearchOption,
        });
    };

    const ratingsModalBody = html`
        <div class="ratings-explained-list">
            ${RATINGS_EXPLAINED.map(
                (r) => html`
                    <div class="ratings-explained-item">
                        <${Chip} type="indicator-chip" css=${r.css} label=${{ text: r.label }} />
                        <p class="ratings-explained-description mb-0">${r.description}</p>
                    </div>
                `
            )}
        </div>
    `;

    const ratingsModalFooter = html`
        <${Button} buttonType="primary" onClick=${closeRatingsModal} label=${{ text: 'Close' }} />
    `;

    return html`
        <${AdvancedFilters}
            ref=${filterRef}
            config=${{
                searchOptions: SarbSearchOptions,
                filterDefaults: SarbFilterDefaults,
                defaultSearchOption: SarbFilterDefaults.defaultSearchOption,
                antecedent: ratingsExplainedButton,
            }}
            callbacks=${{
                search: handleSearch,
                filter: handleFilter,
            }}
        />

        <${Modal}
            header=${{ title: { text: 'Ratings Explained' } }}
            footer=${ratingsModalFooter}
            options=${{
                isVisible: showRatingsModal,
                onCloseEvent: closeRatingsModal,
                size: 'modal-xl',
            }}
        >
            ${ratingsModalBody}
        </${Modal}>
    `;
};
